import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-update-banner.css?inline';

/**
 * `<pk-update-banner>` — dismissible site-wide notice.
 *
 * Used for "v2.1.0 available — release notes →", scheduled-maintenance
 * notices, and similar lightweight one-off banners.
 *
 * Slots:
 *   - default — the message body.
 *   - `cta`   — optional CTA link / button.
 *
 * Attributes:
 *   - `kind`         — `info` | `success` | `warn` (default `info`); drives
 *                      the colour scheme.
 *   - `dismissible`  — boolean; renders an X close button.
 *   - `dismiss-key`  — when set, the dismissed state is persisted under
 *                      `localStorage[dismiss-key]` so the banner stays
 *                      hidden across reloads until the key changes.
 *
 * Events:
 *   - `dismiss` — fired when the user clicks the X. Bubbles + composed.
 */
export class PkUpdateBanner extends HTMLElement {
    static readonly tagName = 'pk-update-banner';

    static get observedAttributes(): string[] {
        return ['kind', 'dismissible', 'dismiss-key'];
    }

    private readonly _root: ShadowRoot;
    private _dismissed = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        // Honour persisted dismissal before first render.
        const key = this.getAttribute('dismiss-key');
        if (key && typeof globalThis.localStorage !== 'undefined') {
            try {
                if (globalThis.localStorage.getItem(key) === '1') {
                    this._dismissed = true;
                }
            } catch {
                /* SecurityError in private modes — fall through */
            }
        }
        this.render();
    }

    attributeChangedCallback(name: string, _old: string | null, _next: string | null): void {
        if (name === 'dismiss-key' && !this._dismissed) {
            // Re-check storage if the key changed.
            const key = this.getAttribute('dismiss-key');
            if (key && typeof globalThis.localStorage !== 'undefined') {
                try {
                    if (globalThis.localStorage.getItem(key) === '1') {
                        this._dismissed = true;
                    }
                } catch { /* */ }
            }
        }
        this.render();
    }

    private _kind(): 'info' | 'success' | 'warn' {
        const v = this.getAttribute('kind') ?? 'info';
        return v === 'success' || v === 'warn' ? v : 'info';
    }

    private readonly _onDismiss = (): void => {
        this._dismissed = true;
        const key = this.getAttribute('dismiss-key');
        if (key && typeof globalThis.localStorage !== 'undefined') {
            try { globalThis.localStorage.setItem(key, '1'); } catch { /* */ }
        }
        this.dispatchEvent(new CustomEvent('dismiss', { bubbles: true, composed: true }));
        this.render();
    };

    private template(): TemplateResult {
        if (this._dismissed) {
            return html`<style>${styles}</style>`;
        }
        const kind        = this._kind();
        const dismissible = this.hasAttribute('dismissible');
        return html`
            <style>${styles}</style>
            <div class="banner kind-${kind}" role="status">
                <div class="message">
                    <slot></slot>
                </div>
                <div class="cta">
                    <slot name="cta"></slot>
                </div>
                ${dismissible ? html`
                    <button
                        type="button"
                        class="close-btn"
                        aria-label="Dismiss"
                        @click=${this._onDismiss}
                    >&#x2715;</button>
                ` : ''}
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkUpdateBanner.tagName)) {
    customElements.define(PkUpdateBanner.tagName, PkUpdateBanner);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-update-banner': PkUpdateBanner;
    }
}
